/**
 * Custom SVG icons for Vibescaping and Mass Planting pages
 */

import React from 'react';
import { cn } from '@/lib/utils';

interface IconProps {
  className?: string;
  selected?: boolean;
}

interface LogoIconProps {
  className?: string;
  pulsing?: boolean;
  color?: string;
}

// Primary color glowing keyframes CSS (exported for use in other components)
export const geminiGlowStyles = `
  @keyframes geminiGlow {
    0%, 100% {
      box-shadow:
        0 0 20px rgba(11, 31, 58, 0.5),
        0 0 40px rgba(11, 31, 58, 0.3),
        0 0 60px rgba(11, 31, 58, 0.2);
      transform: scale(1);
    }
    25% {
      box-shadow:
        0 0 25px rgba(11, 31, 58, 0.6),
        0 0 50px rgba(11, 31, 58, 0.4),
        0 0 70px rgba(11, 31, 58, 0.2);
      transform: scale(1.05);
    }
    50% {
      box-shadow:
        0 0 30px rgba(11, 31, 58, 0.5),
        0 0 55px rgba(11, 31, 58, 0.4),
        0 0 80px rgba(11, 31, 58, 0.3);
      transform: scale(1.1);
    }
    75% {
      box-shadow:
        0 0 25px rgba(11, 31, 58, 0.6),
        0 0 45px rgba(11, 31, 58, 0.5),
        0 0 65px rgba(11, 31, 58, 0.3);
      transform: scale(1.05);
    }
  }

  @keyframes geminiGradientRotate {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  @keyframes geminiIconPulse {
    0%, 100% {
      opacity: 1;
      filter: drop-shadow(0 0 8px rgba(11, 31, 58, 0.8));
    }
    33% {
      opacity: 0.9;
      filter: drop-shadow(0 0 12px rgba(11, 31, 58, 0.9));
    }
    66% {
      opacity: 1;
      filter: drop-shadow(0 0 10px rgba(11, 31, 58, 0.8));
    }
  }

  .gemini-glow-container {
    animation: geminiGlow 2s ease-in-out infinite;
  }

  .gemini-gradient-bg {
    background: linear-gradient(
      135deg,
      #0B1F3A 0%,
      #0B1F3A 25%,
      #0B1F3A 50%,
      #0B1F3A 75%,
      #0B1F3A 100%
    );
    background-size: 400% 400%;
    animation: geminiGradientRotate 3s ease infinite;
  }

  .gemini-icon-glow {
    animation: geminiIconPulse 1.5s ease-in-out infinite;
  }
`;

//Logo Icon (Power Button rotated 180°) with optional Gemini-style glow
export const LogoIcon: React.FC<LogoIconProps> = ({ className, pulsing, color = 'currentColor' }) => {
  // Inject styles once
  React.useEffect(() => {
    if (pulsing) {
      const styleId = 'gemini-glow-styles';
      if (!document.getElementById(styleId)) {
        const styleSheet = document.createElement('style');
        styleSheet.id = styleId;
        styleSheet.textContent = geminiGlowStyles;
        document.head.appendChild(styleSheet);
      }
    }
  }, [pulsing]);

  // For pulsing, we use f-dark (brand primary) color with CSS glow animation
  const baseColor = pulsing ? '#0B1F3A' : color;

  return (
    <svg
      viewBox="0 0 24 24"
      className={cn(className, pulsing && "gemini-icon-glow")}
      fill="none"
      style={{ transform: 'rotate(180deg)' }}
    >
      {/* Arc - the curved part of power icon (open at top) */}
      <path
        d="M18.36 6.64a9 9 0 1 1-12.73 0"
        stroke={baseColor}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Vertical line - the center part of power icon */}
      <line
        x1="12"
        y1="2"
        x2="12"
        y2="12"
        stroke={baseColor}
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
};

// New Chat Icon - single chat bubble
export const NewChatIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="21 34 30 24" className={className} fill="none">
    {/* Chat bubble */}
    <rect
      x="21.22"
      y="34.89"
      width="29.54"
      height="18.78"
      rx="2.12"
      ry="2.12"
      fill={selected ? '#0B1F3A' : '#51607A'}
    />
    {/* Bubble pointer */}
    <polygon
      points="24.59 53.69 23.69 56.9 28.42 53.69 24.59 53.69"
      fill={selected ? '#0B1F3A' : '#51607A'}
    />
  </svg>
);

// Chats Icon - overlapping chat bubbles
export const ChatsIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="16 32 40 34" className={className} fill="none">
    {/* Back chat bubble (light gray) */}
    <rect
      x="25.7"
      y="32.56"
      width="29.54"
      height="18.78"
      rx="2.12"
      ry="2.12"
      fill={selected ? 'rgba(11, 31, 58, 0.5)' : '#cacaca'}
    />
    {/* Back bubble pointer */}
    <polygon
      points="51.87 51.36 52.76 54.57 48.04 51.36 51.87 51.36"
      fill={selected ? 'rgba(11, 31, 58, 0.5)' : '#cacaca'}
    />
    {/* Front chat bubble (darker) */}
    <rect
      x="16.74"
      y="42.17"
      width="29.54"
      height="18.78"
      rx="2.12"
      ry="2.12"
      fill={selected ? '#0B1F3A' : '#51607A'}
    />
    {/* Front bubble pointer */}
    <polygon
      points="20.1 60.97 19.21 64.18 23.94 60.97 20.1 60.97"
      fill={selected ? '#0B1F3A' : '#51607A'}
    />
  </svg>
);

// FieldSmart Icon - bucket with plant and location pin
export const FieldSmartIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="19 28 33 40" className={className} fill="none">
    {/* Trapezoid bucket shape */}
    <polygon
      points="42.21 66.62 29.77 66.62 23.17 52.38 48.81 52.38 42.21 66.62"
      fill={selected ? '#0B1F3A' : '#51607A'}
    />
    {/* Plant leaves - left side */}
    <path
      d="M35.89,39.13c-.13.59-.12,1.52-.17,2.16-.03.33-.53-.18-.62-.28-.43-.47-.81-1.13-1.29-1.62-3.03-3.06-8.2-3.59-12.26-2.92-.36.06-2.02.29-2.2.5-.35.43.95,1.08,1.25,1.37,1.94,1.79,2.03,5,3.97,6.9,1.74,1.7,5.44,2.77,7.68,1.69.97-.47,1-.63.56-1.56-1.83-3.9-6.41-6.22-10.17-7.57-.18-.07-.31.05-.23-.25.39.06.85.02,1.24.11,3.29.78,8.57,3.52,10.1,6.72,1.33,2.78.73,4.92.65,7.81-.02.85.22,4.19-.15,4.67-.31.41-2.31.4-2.9.5-2.3.38.9,1.79-1.09,2.99s15.56-.05,11.56-1.78l-4.44-1.36c-.37-.42-.03-3.03-.03-3.7-.01-4.29-.36-7.8,1.52-11.83,1.74-3.73,4.18-6.99,7.69-9.2.27-.17,1.83-1.07,1.99-1.09.11-.02.41-.02.48.09-3.15,2.01-6.01,4.6-7.84,7.95-.66,1.21-1.9,4.12-1.84,5.43.03.63,1.28,1.02,1.84,1.15,2.21.5,4.49-.23,6.1-1.77,3.54-3.37,3.06-8.83,4.56-13.11.2-.56.61-1.22.77-1.7.04-.11.11-.21.03-.33-.96-.43-1.93-.1-2.89.05-4.36.67-10.3,3.3-12.65,7.28-.28.47-1.12,2.24-1.22,2.71Z"
      fill={selected ? '#0B1F3A' : '#51607A'}
    />
    {/* Plant leaves - right side */}
    <path
      d="M35.89,39.13c.1-.47.95-2.24,1.22-2.71,2.35-3.97,8.29-6.61,12.65-7.28.96-.15,1.93-.48,2.89-.05.08.12.01.22-.03.33-.16.48-.58,1.14-.77,1.7-1.51,4.28-1.02,9.74-4.56,13.11-1.61,1.54-3.89,2.27-6.1,1.77-.56-.13-1.81-.52-1.84-1.15-.07-1.31,1.18-4.22,1.84-5.43,1.83-3.35,4.69-5.94,7.84-7.95-.06-.11-.37-.1-.48-.09-.17.02-1.72.92-1.99,1.09-3.52,2.21-5.95,5.46-7.69,9.2-1.88,4.04-1.53,7.54-1.52,11.83,0,.67-.34,3.29.03,3.7l4.44,1.36s-12.45-.02-10.46-1.21-2.3.38,0,0c.59-.1,2.59-.09,2.9-.5.37-.49.12-3.82.15-4.67.08-2.89.67-5.03-.65-7.81-1.53-3.2-6.81-5.94-10.1-6.72-.38-.09-.84-.04-1.24-.11-.08.3.05.19.23.25,3.76,1.35,8.34,3.67,10.17,7.57.44.93.41,1.09-.56,1.56-2.24,1.08-5.94,0-7.68-1.69-1.94-1.9-2.04-5.11-3.97-6.9-.3-.28-1.6-.94-1.25-1.37.18-.22,1.84-.45,2.2-.5,4.06-.66,9.23-.14,12.26,2.92.48.49.86,1.15,1.29,1.62.09.1.59.61.62.28.05-.64.04-1.58.17-2.16Z"
      fill={selected ? '#0B1F3A' : '#636462'}
    />
    {/* Location pin - outer shape */}
    <path
      d="M35.8,53.8c-.9.08-1.62.82-1.7,1.71.07,1.16-.09,2.41,0,3.56.18,2.25,3.61,2.29,3.78-.06-.07-1.13.09-2.35,0-3.47-.08-1.06-1.02-1.83-2.08-1.74ZM35.67,62.02v1.39l-.04.04h-1.59s-.13.09-.14.11c-.1.15-.03.44.17.47s.49,0,.7,0c.91.03,2.1.11,2.99.03.21-.02.41-.13.37-.37-.03-.22-.21-.26-.4-.27-.41-.03-.84.03-1.25.02-.04,0-.15-.02-.17-.04v-1.35c1.58-.21,2.75-1.44,2.84-3.03.02-.31.04-1.02,0-1.31-.06-.34-.47-.36-.6-.06-.01,1.06.13,2.02-.61,2.89-1.25,1.47-3.64,1.04-4.31-.76-.26-.72-.09-1.29-.15-2-.04-.47-.57-.45-.62-.06s-.03,1.29.04,1.67c.22,1.38,1.4,2.5,2.79,2.62Z"
      fill="#fff"
    />
    {/* Location pin - inner circle */}
    <path
      d="M35.8,53.8c1.05-.09,1.99.68,2.08,1.74.09,1.12-.07,2.34,0,3.47-.16,2.34-3.6,2.31-3.78.06-.09-1.14.07-2.4,0-3.56.08-.89.8-1.63,1.7-1.71Z"
      fill="#fff"
    />
    {/* Location pin - bottom details */}
    <path
      d="M35.67,62.02c-1.39-.12-2.57-1.25-2.79-2.62-.06-.38-.08-1.29-.04-1.67s.58-.41.62.06c.06.71-.11,1.29.15,2,.66,1.8,3.06,2.23,4.31.76.74-.87.59-1.83.61-2.89.13-.3.54-.29.6.06.05.29.03,1,0,1.31-.09,1.59-1.26,2.82-2.83,3.03v1.35s.12.04.16.04c.41.01.84-.05,1.25-.02.19.01.37.06.4.27.04.24-.16.35-.37.37-.89.08-2.08,0-2.99-.03-.21,0-.5.03-.7,0s-.27-.32-.17-.47c.01-.02.13-.11.14-.11h1.59l.04-.04v-1.39Z"
      fill="#fff"
    />
  </svg>
);

// Vibescaping Icon - tablet/device with grid pattern
export const VibescapingIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="21 31 30 34" className={className} fill="none">
    {/* Outer rounded rectangle (device frame) */}
    <rect
      x="21.47"
      y="31.22"
      width="29.03"
      height="33.54"
      rx="2.5"
      ry="2.5"
      stroke={selected ? '#0B1F3A' : '#51607A'}
      strokeWidth="1.31"
      strokeMiterlimit="10"
      fill="none"
    />
    {/* Home button circle */}
    <circle cx="35.99" cy="61.89" r="1.01" fill={selected ? '#0B1F3A' : '#51607A'} />
    {/* Screen background */}
    <rect x="21.72" y="37.85" width="28.54" height="18.11" fill={selected ? '#0B1F3A' : '#636462'} />
    {/* Center divider line */}
    <line
      x1="35.99"
      y1="35.43"
      x2="35.99"
      y2="58.39"
      stroke={selected ? '#0B1F3A' : '#51607A'}
      strokeWidth="0.62"
      strokeMiterlimit="10"
    />
    {/* Left grid cell (dashed) */}
    <rect
      x="23.38"
      y="42.2"
      width="11.05"
      height="10.05"
      fill={selected ? 'rgba(11, 31, 58, 0.3)' : '#51607A'}
      stroke={selected ? '#fff' : '#fff'}
      strokeDasharray="2.1"
      strokeMiterlimit="5"
      strokeWidth="0.4"
    />
    {/* Right grid cell (solid white) */}
    <rect x="37.55" y="42.2" width="11.05" height="10.05" fill="#fff" />
  </svg>
);

// Sliders/Adjustments Icon
export const SlidersIcon: React.FC<IconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    {/* Top slider - circle on right */}
    <line x1="3" y1="5" x2="14" y2="5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="17" cy="5" r="2.5" stroke="currentColor" strokeWidth="2" fill="none" />
    <line x1="20" y1="5" x2="21" y2="5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />

    {/* Middle slider - circle on left */}
    <line x1="3" y1="12" x2="4" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="7" cy="12" r="2.5" stroke="currentColor" strokeWidth="2" fill="none" />
    <line x1="10" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />

    {/* Bottom slider - circle on right */}
    <line x1="3" y1="19" x2="14" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="17" cy="19" r="2.5" stroke="currentColor" strokeWidth="2" fill="none" />
    <line x1="20" y1="19" x2="21" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// No Image Placeholder Icon - for use when image fails to load
export const NoImagePlaceholder: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none">
    <rect fill="#1E293B" width="100" height="100" rx="8" />
    {/* Image icon outline */}
    <rect x="25" y="30" width="50" height="40" rx="4" stroke="#64748B" strokeWidth="2" fill="none" />
    {/* Mountain/landscape shape */}
    <path d="M25 60 L40 48 L50 55 L65 40 L75 52 L75 66 C75 68.2 73.2 70 71 70 L29 70 C26.8 70 25 68.2 25 66 Z" fill="#64748B" opacity="0.5" />
    {/* Sun circle */}
    <circle cx="62" cy="42" r="6" fill="#64748B" opacity="0.5" />
    {/* Diagonal line (broken image indicator) */}
    <line x1="30" y1="75" x2="70" y2="75" stroke="#64748B" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Data URI for use in img onError handlers
export const NO_IMAGE_PLACEHOLDER_URI = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect fill="%231E293B" width="100" height="100" rx="8"/><rect x="25" y="30" width="50" height="40" rx="4" stroke="%2364748B" stroke-width="2" fill="none"/><path d="M25 60 L40 48 L50 55 L65 40 L75 52 L75 66 C75 68.2 73.2 70 71 70 L29 70 C26.8 70 25 68.2 25 66 Z" fill="%2364748B" opacity="0.5"/><circle cx="62" cy="42" r="6" fill="%2364748B" opacity="0.5"/></svg>';

// ============================================================================
// SUN EXPOSURE ICONS
// ============================================================================

// Full Sun Icon - sun with rays (outline style)
export const SunFullIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    {/* Center circle */}
    <circle cx="12" cy="12" r="4" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" fill="none" />
    {/* Rays */}
    <path d="M12 2v3" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
    <path d="M12 19v3" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
    <path d="M5.64 5.64l2.12 2.12" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
    <path d="M16.24 16.24l2.12 2.12" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
    <path d="M2 12h3" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
    <path d="M19 12h3" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
    <path d="M5.64 18.36l2.12-2.12" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
    <path d="M16.24 7.76l2.12-2.12" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Part Sun Icon - half-circle sun with rays on left side only
export const SunPartIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    <line x1="12" y1="1" x2="12" y2="4" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
    <line x1="4.22" y1="4.22" x2="6.34" y2="6.34" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
    <line x1="1" y1="12" x2="4" y2="12" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
    <line x1="4.22" y1="19.78" x2="6.34" y2="17.66" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
    <path d="M12 7a5 5 0 0 1 0 10" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" fill="none" />
    <path d="M12 7a5 5 0 0 0 0 10" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
  </svg>
);

// Full Shade Icon - solid filled circle
export const ShadeFullIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    {/* Solid filled circle */}
    <circle cx="12" cy="12" r="8" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
  </svg>
);

// ============================================================================
// PLANT DENSITY ICONS (Agave-style plants)
// ============================================================================

// Newly Planted Icon - young plant with 5 leaves and ground line
export const NewlyPlantedIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="0 0 40 40" className={className} fill="none">
    {/* Center leaf */}
    <path d="M20 28 Q20 18 20 10 Q20 18 20 28" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M20 28 C18 20 17 14 20 10 C23 14 22 20 20 28" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    {/* Inner left leaf */}
    <path d="M20 28 C16 22 12 18 14 12" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M20 28 C14 24 10 20 14 12 C16 18 18 22 20 28" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    {/* Inner right leaf */}
    <path d="M20 28 C24 22 28 18 26 12" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M20 28 C26 24 30 20 26 12 C24 18 22 22 20 28" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    {/* Outer left leaf */}
    <path d="M20 28 C14 26 8 24 8 18" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M20 28 C12 28 6 26 8 18 C10 24 14 26 20 28" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    {/* Outer right leaf */}
    <path d="M20 28 C26 26 32 24 32 18" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M20 28 C28 28 34 26 32 18 C30 24 26 26 20 28" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    {/* Curved ground line */}
    <path d="M6 34 Q20 28 34 34" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
  </svg>
);

// Mature Plant Icon - large plant with 7 leaves and ground line
export const MaturePlantIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="0 0 40 40" className={className} fill="none">
    {/* Center leaf (tallest) */}
    <path d="M20 30 Q20 16 20 4 Q20 16 20 30" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M20 30 C17 20 16 10 20 4 C24 10 23 20 20 30" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    {/* Inner left leaf */}
    <path d="M20 30 C15 22 11 14 14 6" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M20 30 C13 24 9 16 14 6 C15 14 17 22 20 30" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    {/* Inner right leaf */}
    <path d="M20 30 C25 22 29 14 26 6" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M20 30 C27 24 31 16 26 6 C25 14 23 22 20 30" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    {/* Middle left leaf */}
    <path d="M20 30 C12 26 6 20 6 12" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M20 30 C10 28 4 22 6 12 C8 20 14 26 20 30" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    {/* Middle right leaf */}
    <path d="M20 30 C28 26 34 20 34 12" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M20 30 C30 28 36 22 34 12 C32 20 26 26 20 30" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    {/* Outer left leaf (most horizontal) */}
    <path d="M20 30 C10 30 2 28 2 20" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M20 30 C8 32 0 30 2 20 C4 28 12 30 20 30" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    {/* Outer right leaf (most horizontal) */}
    <path d="M20 30 C30 30 38 28 38 20" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M20 30 C32 32 40 30 38 20 C36 28 28 30 20 30" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
    {/* Wider curved ground line */}
    <path d="M2 38 Q20 30 38 38" stroke={selected ? '#0B1F3A' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" fill="none" />
  </svg>
);

// ============================================================================
// DESIGN LAYOUT ICONS
// ============================================================================

// Fully Organic Icon - scattered random dots
export const FullyOrganicIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="0 0 40 40" className={className} fill="none">
    {/* Scattered random dots */}
    <circle cx="8" cy="12" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="18" cy="8" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="32" cy="14" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="12" cy="24" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="26" cy="20" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="8" cy="34" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="22" cy="32" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="34" cy="28" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
  </svg>
);

// Semi-Organic Icon - semi-scattered with some grouping
export const SemiOrganicIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="0 0 40 40" className={className} fill="none">
    {/* Semi-scattered with some grouping */}
    <circle cx="10" cy="10" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="16" cy="12" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="30" cy="10" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="10" cy="22" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="20" cy="20" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="30" cy="22" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="14" cy="32" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="26" cy="32" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
  </svg>
);

// Structured Naturalistic Icon - more structured rows with slight variation
export const StructuredNaturalisticIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="0 0 40 40" className={className} fill="none">
    {/* More structured rows with slight variation */}
    <circle cx="8" cy="8" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="20" cy="8" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="32" cy="8" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="14" cy="20" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="26" cy="20" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="8" cy="32" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="20" cy="32" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="32" cy="32" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
  </svg>
);

// Formal Structured Icon - perfect 3x3 grid
export const FormalStructuredIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="0 0 40 40" className={className} fill="none">
    {/* Perfect grid */}
    <circle cx="8" cy="8" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="20" cy="8" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="32" cy="8" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="8" cy="20" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="20" cy="20" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="32" cy="20" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="8" cy="32" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="20" cy="32" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
    <circle cx="32" cy="32" r="2.5" fill={selected ? '#0B1F3A' : '#9CA3AF'} />
  </svg>
);

// ============================================================================
// VALUE TO COMPONENT NAME MAPPING
// Maps API value field to React component names
// ============================================================================

export const VALUE_TO_COMPONENT_NAME: Record<string, string> = {
  // Sun Exposure - value field from API
  'full-sun': 'SunFullIcon',
  'part-sun': 'SunPartIcon',
  'full-shade': 'ShadeFullIcon',
  // Plant Density - value field from API
  'newly-planted': 'NewlyPlantedIcon',
  'mature': 'MaturePlantIcon',
  // Design Layout - value field from API
  'fully-organic': 'FullyOrganicIcon',
  'semi-organic': 'SemiOrganicIcon',
  'structured-naturalistic': 'StructuredNaturalisticIcon',
  'formal-structured': 'FormalStructuredIcon',
};

// Helper function to get component name from value
export const getComponentNameFromValue = (value: string): string => {
  return VALUE_TO_COMPONENT_NAME[value] || '';
};

// ============================================================================
// ICON COMPONENT MAP - Maps React component names to components
// ============================================================================

export const ICON_COMPONENT_MAP: Record<string, React.FC<IconProps>> = {
  // General
  'FieldSmartIcon': FieldSmartIcon,
  'VibescapingIcon': VibescapingIcon,
  'NewChatIcon': NewChatIcon,
  'ChatsIcon': ChatsIcon,
  // Sun Exposure
  'SunFullIcon': SunFullIcon,
  'SunPartIcon': SunPartIcon,
  'ShadeFullIcon': ShadeFullIcon,
  // Plant Density
  'NewlyPlantedIcon': NewlyPlantedIcon,
  'MaturePlantIcon': MaturePlantIcon,
  // Design Layout
  'FullyOrganicIcon': FullyOrganicIcon,
  'SemiOrganicIcon': SemiOrganicIcon,
  'StructuredNaturalisticIcon': StructuredNaturalisticIcon,
  'FormalStructuredIcon': FormalStructuredIcon,
};

// Helper function to render icon by name
export const renderIconByName = (
  iconName: string,
  className?: string,
  selected?: boolean
): React.ReactNode => {
  const IconComponent = ICON_COMPONENT_MAP[iconName];
  if (IconComponent) {
    return <IconComponent className={className} selected={selected} />;
  }
  // Fallback for unknown icons - return null
  return null;
};
